import React, {
  Ref,
  JSX,
  useMemo,
  MouseEvent,
  KeyboardEvent,
  useCallback,
  ReactElement,
  JSXElementConstructor
} from 'react';
import ReactDatePicker from 'react-datepicker';
import { appService } from '../../../Hooks/Contexts';
import { DataTestIdProp } from '../../../../Models/DataTestIdProp';
import { ConfigModel } from '@ibe/services';
import useConfig from '../../../Hooks/useConfig';
import { CustomDateDisplay, CustomDisplayProps } from './CustomDateDisplay';
import {
  DateWithKeyboardSupport,
  OnChangeDate,
  OnChangeDateIntersection
} from './DateWithKeyboardSupport';
import { CustomsProps, DatePickerProps } from './DatePickerInModal';
import { Day } from 'date-fns';

export type ReactDatePickerRendererProps = {
  // eslint-disable-next-line
  reactDatePickerRef?: Ref<any> | undefined;
  showInModal?: boolean;
  toggleDropdown?: () => void;
} & DatePickerProps &
  DataTestIdProp &
  CustomsProps &
  CustomDisplayProps;

const getCalendarStartDay = (configuration: ConfigModel, lang: string): number => {
  const weekStartsOn = configuration.calendar?.weekStartsOn || {};
  return weekStartsOn[lang] ?? weekStartsOn[lang.substring(2)] ?? weekStartsOn?.en ?? 1;
};

const ReactDatePickerRenderer = (props: ReactDatePickerRendererProps): JSX.Element => {
  const {
    reactDatePickerRef,
    dataTestId,
    locale,
    customCalendarContainer,
    renderCustomHeader,
    onChange,
    onCalendarClose,
    onCalendarOpen,
    customInput,
    placeholderText,
    showInModal,
    withKeyboardSupport,
    toggleDropdown,
    date,
    displayFormat,
    theme,
    className,
    onInputChange,
    minDate,
    maxDate,
    inputClassName,
    useFormValidation,
    setValueToNullWhenFormatIsIncorrect,
    focusSelectedMonth,
    inline,
    noDatePickerOverflow,
    onMonthChange,
    withHeader,
    renderDayContents,
    noInitialValueInput
  } = props;

  const config = useConfig();

  const popperModifiers = useMemo(
    () => [
      {
        name: 'flip',
        options: {
          flipVariations: false,
          allowedAutoPlacements: ['bottom']
        },
        fn: (): Promise<{}> => {
          return new Promise(resolve => {
            resolve({});
          });
        }
      },
      ...(noDatePickerOverflow
        ? [
            {
              name: 'preventOverflow',
              options: {
                rootBoundary: 'viewport'
              },
              fn: (): Promise<{}> => {
                return new Promise(resolve => {
                  resolve({});
                });
              }
            }
          ]
        : [])
    ],
    [noDatePickerOverflow]
  );

  const handleChange = useCallback(
    (
      dates: OnChangeDate | Date[],
      event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement> | undefined
    ): void => {
      if (showInModal) {
        if (Array.isArray(dates)) {
          if (dates.every(element => element)) {
            if (toggleDropdown) {
              toggleDropdown();
            }
          }
        }
      }
      if (!!onChange) {
        onChange(dates as OnChangeDateIntersection, event);
      }
    },
    [onChange, showInModal, toggleDropdown]
  );

  return (
    <ReactDatePicker
      {...props}
      selectsRange={props.selectsRange as undefined}
      ref={reactDatePickerRef}
      data-testid={dataTestId}
      locale={locale}
      calendarContainer={customCalendarContainer}
      renderCustomHeader={withHeader ? renderCustomHeader : undefined}
      renderDayContents={renderDayContents}
      onChange={handleChange}
      onCalendarOpen={onCalendarOpen}
      onCalendarClose={onCalendarClose}
      customInput={
        (customInput ||
          (withKeyboardSupport && (
            <DateWithKeyboardSupport
              date={date}
              format={displayFormat}
              theme={theme}
              dataTestId={dataTestId}
              placeholder={placeholderText || ''}
              className={className}
              onInputChange={onInputChange}
              minDate={minDate}
              maxDate={maxDate}
              inputClassName={inputClassName}
              useFormValidation={useFormValidation}
              setValueToNullWhenFormatIsIncorrect={setValueToNullWhenFormatIsIncorrect}
              noInitialValueInput={noInitialValueInput}
            />
          )) ||
          ((date || placeholderText) && (
            <CustomDateDisplay
              date={date}
              format={displayFormat}
              theme={theme}
              dataTestId={dataTestId}
              placeholder={placeholderText || ''}
              className={className}
            />
          ))) as ReactElement<unknown, string | JSXElementConstructor<unknown>>
      }
      placeholderText={placeholderText}
      selected={date}
      popperPlacement="bottom-start"
      focusSelectedMonth={focusSelectedMonth}
      popperClassName="iso__date-picker__popper"
      popperModifiers={popperModifiers}
      minDate={minDate as Date | undefined}
      maxDate={maxDate as Date | undefined}
      calendarStartDay={
        getCalendarStartDay(config, appService.lang || config.defaultLanguage) as Day | undefined
      }
      onMonthChange={onMonthChange}
      inline={inline}
    />
  );
};

export default ReactDatePickerRenderer;
